import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  Package,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  History,
  Archive,
  Filter,
  X,
  ArrowUpDown,
  Boxes,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import {
  useInventory,
  useLowStockItems,
  useStockMovements,
  useAdjustStock,
  useCreateInventoryItem,
  useUpdateInventoryItem,
} from "../api";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  ScrollArea,
} from "@/components/ui";
import {
  PageHeader,
  PageLoader,
  PageError,
  EmptyState,
  PermissionButton,
} from "@/components/common";
import { useDebounce } from "@/hooks";
import { cn, formatRelativeTime, generateSku } from "@/lib/utils";
import type { InventoryItem, InventoryTransaction } from "@/types";

const INVENTORY_UNITS = [
  { value: "g", label: "Grams (g)" },
  { value: "ml", label: "Milliliters (ml)" },
  { value: "pieces", label: "Pieces" },
  { value: "oz", label: "Ounces (oz)" },
] as const;

type InventoryUnit = (typeof INVENTORY_UNITS)[number]["value"];

export function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as "items" | "history") || "items";

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col md:h-screen">
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 md:px-6">
          <PageHeader
            title="Inventory"
            description="Track stock levels and manage inventory"
          />
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="mt-4"
          >
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="items" className="gap-2">
                <Package className="h-4 w-4" />
                <span className="hidden sm:inline">Items</span>
              </TabsTrigger>
              <TabsTrigger value="history" className="gap-2">
                <History className="h-4 w-4" />
                <span className="hidden sm:inline">History</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="container mx-auto px-4 py-6 md:px-6">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsContent value="items" className="mt-0">
              <InventoryItemsView />
            </TabsContent>
            <TabsContent value="history" className="mt-0">
              <StockHistoryView />
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>
    </div>
  );
}

function InventoryItemsView() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<InventoryItem | null>(null);
  const [adjustItemId, setAdjustItemId] = useState<string | null>(null);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const {
    data: inventoryData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useInventory({
    search: debouncedSearch || undefined,
    page,
    limit: 50,
  });
  const { data: lowStockItems } = useLowStockItems();
  const adjustStock = useAdjustStock();
  const createItem = useCreateInventoryItem();
  const updateItem = useUpdateInventoryItem();

  const inventory = inventoryData?.data ?? [];
  const pagination = inventoryData?.pagination;

  const displayedItems = showLowStockOnly
    ? inventory.filter((item) => item.currentStock <= item.minStockLevel)
    : inventory;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const handleAdjustStock = async (data: {
    quantity: number;
    reason: string;
    notes?: string;
  }) => {
    if (!adjustItemId) return;
    try {
      await adjustStock.mutateAsync({ id: adjustItemId, data });
      toast.success("Stock adjusted successfully");
      setAdjustItemId(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to adjust stock",
      );
    }
  };

  const handleCreateItem = async (data: {
    name: string;
    sku: string;
    unit: InventoryUnit;
    currentStock: number;
    minStockLevel: number;
  }) => {
    try {
      await createItem.mutateAsync(data);
      toast.success("Item created successfully");
      setIsCreateOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create item");
    }
  };

  const handleUpdateItem = async (data: {
    name: string;
    sku: string;
    unit: InventoryUnit;
    minStockLevel: number;
  }) => {
    if (!editItem) return;
    try {
      await updateItem.mutateAsync({ id: editItem.id, data });
      toast.success("Item updated successfully");
      setEditItem(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update item");
    }
  };

  if (isLoading && !inventory.length) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load inventory" onRetry={refetch} />;

  const adjustItem = adjustItemId
    ? inventory.find((i) => i.id === adjustItemId)
    : null;

  const totalItems = pagination?.total ?? 0;
  const lowStockCount = lowStockItems?.length ?? 0;
  const inStockCount = totalItems - lowStockCount;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          icon={Boxes}
          label="Total Items"
          value={totalItems}
          color="default"
        />
        <StatsCard
          icon={Package}
          label="In Stock"
          value={inStockCount}
          color="success"
        />
        <StatsCard
          icon={AlertTriangle}
          label="Low Stock"
          value={lowStockCount}
          color="warning"
          onClick={() => setShowLowStockOnly(!showLowStockOnly)}
          active={showLowStockOnly}
        />
        <StatsCard
          icon={Archive}
          label="Categories"
          value={4}
          subtitle="tracked"
          color="default"
        />
      </div>
      {lowStockItems && lowStockItems.length > 0 && !showLowStockOnly && (
        <div className="relative overflow-hidden rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-stone-50 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-800">
                  {lowStockItems.length} item
                  {lowStockItems.length > 1 ? "s" : ""} running low
                </h3>
                <p className="text-sm text-stone-600">
                  {lowStockItems
                    .slice(0, 3)
                    .map((i) => i.name)
                    .join(", ")}
                  {lowStockItems.length > 3 &&
                    ` and ${lowStockItems.length - 3} more`}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLowStockOnly(true)}
              className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            >
              View All
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          {showLowStockOnly && (
            <Badge
              variant="secondary"
              className="gap-1 bg-amber-100 text-amber-800 hover:bg-amber-200"
            >
              <Filter className="h-3 w-3" />
              Low Stock Only
              <button
                onClick={() => setShowLowStockOnly(false)}
                className="ml-1 hover:text-amber-900"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <PermissionButton
            permission="inventory.update"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Item
          </PermissionButton>
        </div>
      </div>
      <div
        className={
          isFetching
            ? "opacity-60 transition-opacity duration-200"
            : "transition-opacity duration-200"
        }
      >
        {!displayedItems.length ? (
          <EmptyState
            icon={Package}
            title={
              showLowStockOnly ? "No low stock items" : "No inventory items"
            }
            description={
              search
                ? "Try adjusting your search"
                : showLowStockOnly
                  ? "All items are adequately stocked"
                  : "Add items to start tracking inventory"
            }
            action={
              !search && !showLowStockOnly ? (
                <PermissionButton
                  permission="inventory.update"
                  onClick={() => setIsCreateOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Item
                </PermissionButton>
              ) : showLowStockOnly ? (
                <Button
                  variant="outline"
                  onClick={() => setShowLowStockOnly(false)}
                >
                  View All Items
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {displayedItems.map((item) => (
                <InventoryItemCard
                  key={item.id}
                  item={item}
                  onEdit={() => setEditItem(item)}
                  onAdjust={() => setAdjustItemId(item.id)}
                />
              ))}
            </div>
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <CreateItemDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={handleCreateItem}
        isLoading={createItem.isPending}
      />
      <EditItemDialog
        open={!!editItem}
        onOpenChange={(open) => !open && setEditItem(null)}
        item={editItem}
        onSubmit={handleUpdateItem}
        isLoading={updateItem.isPending}
      />
      <AdjustStockDialog
        open={!!adjustItemId}
        onOpenChange={(open) => !open && setAdjustItemId(null)}
        item={adjustItem}
        onSubmit={handleAdjustStock}
        isLoading={adjustStock.isPending}
      />
    </div>
  );
}

interface StatsCardProps {
  icon: React.ElementType;
  label: string;
  value: number | string;
  subtitle?: string;
  color: "default" | "success" | "warning";
  onClick?: () => void;
  active?: boolean;
}

const colorClasses = {
  default: "bg-stone-100 text-stone-600",
  success: "bg-amber-50 text-amber-800",
  warning: "bg-rose-50 text-rose-700",
};

function StatsCard({
  icon: Icon,
  label,
  value,
  subtitle,
  color,
  onClick,
  active,
}: StatsCardProps) {
  return (
    <Card
      className={cn(
        "transition-all",
        onClick && "cursor-pointer hover:shadow-md",
        active && "ring-2 ring-primary",
      )}
      onClick={onClick}
    >
      <CardContent className="flex items-center gap-4 p-4">
        <div
          className={cn(
            "flex h-12 w-12 items-center justify-center rounded-lg",
            colorClasses[color],
          )}
        >
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-muted-foreground">
            {label}
            {subtitle && <span className="ml-1">{subtitle}</span>}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

interface InventoryItemCardProps {
  item: InventoryItem;
  onEdit: () => void;
  onAdjust: () => void;
}

function InventoryItemCard({ item, onEdit, onAdjust }: InventoryItemCardProps) {
  const isLowStock = item.currentStock <= item.minStockLevel;
  const stockPercent = Math.min(
    100,
    (item.currentStock / Math.max(item.minStockLevel * 2, 1)) * 100,
  );

  return (
    <Card
      className={cn(
        "group transition-all hover:shadow-md",
        isLowStock && "border-amber-300",
      )}
    >
      <CardContent className="p-4">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{item.name}</h3>
            <p className="text-xs text-muted-foreground font-mono">
              {item.sku}
            </p>
          </div>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            <PermissionButton
              permission="inventory.update"
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </PermissionButton>
            <Badge
              variant="outline"
              className={cn(
                isLowStock
                  ? "border-rose-300 bg-rose-50 text-rose-700"
                  : "border-amber-300 bg-amber-50 text-amber-800",
              )}
            >
              {isLowStock ? "Low" : "OK"}
            </Badge>
          </div>
        </div>
        <div className="mb-3">
          <div className="mb-1.5 flex items-baseline justify-between">
            <span className="text-2xl font-bold">{item.currentStock}</span>
            <span className="text-sm text-muted-foreground">
              / {item.minStockLevel} min
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-stone-200">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                isLowStock ? "bg-rose-400" : "bg-amber-600",
              )}
              style={{ width: `${stockPercent}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{item.unit}</p>
        </div>
        <PermissionButton
          permission="inventory.update"
          mode="disable"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onAdjust}
        >
          <ArrowUpDown className="mr-2 h-4 w-4" />
          Adjust Stock
        </PermissionButton>
      </CardContent>
    </Card>
  );
}

function StockHistoryView() {
  const [search, setSearch] = useState("");
  const [selectedIngredientId, setSelectedIngredientId] = useState<
    string | undefined
  >(undefined);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(search, 300);

  const { data: inventoryData } = useInventory({ limit: 1000 });
  const inventory = inventoryData?.data ?? [];
  const {
    data: movementsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useStockMovements({
    ingredientId: selectedIngredientId,
    search: debouncedSearch || undefined,
    page,
    limit: 20,
  });

  const movements = movementsData?.data ?? [];
  const pagination = movementsData?.pagination;

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedIngredientId]);

  const handleItemChange = (value: string) => {
    setSelectedIngredientId(value === "all" ? undefined : value);
  };

  const clearFilters = () => {
    setSelectedIngredientId(undefined);
    setSearch("");
  };

  if (isLoading && !movements.length) return <PageLoader />;
  if (error)

    return (
      <PageError message="Failed to load stock history" onRetry={refetch} />
    );

  const hasFilters = !!selectedIngredientId || !!search;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Filter by Item
              </Label>
              <Select
                value={selectedIngredientId ?? "all"}
                onValueChange={handleItemChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All items" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  {inventory.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Search
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by order #, user..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {pagination?.total ?? 0} movement
          {(pagination?.total ?? 0) !== 1 ? "s" : ""} found
        </p>
      </div>
      <div
        className={
          isFetching
            ? "opacity-60 transition-opacity duration-200"
            : "transition-opacity duration-200"
        }
      >
        {!movements.length ? (
          <EmptyState
            icon={History}
            title="No stock movements"
            description={
              hasFilters
                ? "Try adjusting your filters"
                : "Stock adjustments will appear here"
            }
            action={
              hasFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="space-y-3">
            {movements.map((movement) => (
              <MovementCard key={movement.id} movement={movement} />
            ))}

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      let pageNum: number;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={pageNum === page ? "default" : "outline"}
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    },
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface MovementCardProps {
  movement: InventoryTransaction;
}

function MovementCard({ movement }: MovementCardProps) {
  const isPositive = movement.quantity > 0;
  const userName = movement.user
    ? `${movement.user.firstName} ${movement.user.lastName}`.trim()
    : "System";

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full",
                isPositive ? "bg-amber-100" : "bg-stone-100",
              )}
            >
              {isPositive ? (
                <TrendingUp className="h-5 w-5 text-amber-700" />
              ) : (
                <TrendingDown className="h-5 w-5 text-stone-600" />
              )}
            </div>
            <div className="min-w-0">
              <h4 className="font-medium">
                {movement.ingredient?.name ?? "Unknown Item"}
              </h4>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <Badge variant="outline" className="capitalize text-xs">
                  {movement.type}
                </Badge>
                <span>by {userName}</span>
                {movement.order && (
                  <>
                    <span>•</span>
                    <Link
                      to={`/orders?id=${movement.order.id}`}
                      className="text-primary hover:underline"
                    >
                      #{movement.order.orderNumber}
                    </Link>
                  </>
                )}
              </div>
              {movement.notes && (
                <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                  {movement.notes}
                </p>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <p
              className={cn(
                "text-lg font-bold",
                isPositive ? "text-amber-700" : "text-stone-600",
              )}
            >
              {isPositive ? "+" : ""}
              {movement.quantity}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatRelativeTime(movement.createdAt)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface CreateItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    name: string;
    sku: string;
    unit: InventoryUnit;
    currentStock: number;
    minStockLevel: number;
  }) => void;
  isLoading: boolean;
}

function CreateItemDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
}: CreateItemDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    unit: "g" as InventoryUnit,
    currentStock: 0,
    minStockLevel: 10,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        name: "",
        sku: "",
        unit: "g",
        currentStock: 0,
        minStockLevel: 10,
      });
    }
  }, [open]);

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      sku:
        prev.sku === "" || prev.sku === generateSku(prev.name, "INV")
          ? generateSku(name, "INV")
          : prev.sku,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Add Inventory Item
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Item Name</Label>
              <Input
                id="name"
                placeholder="e.g., Coffee Beans"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sku">SKU</Label>
              <Input
                id="sku"
                placeholder="Auto-generated"
                value={formData.sku}
                onChange={(e) =>
                  setFormData({ ...formData, sku: e.target.value })
                }
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="unit">Unit of Measure</Label>
            <Select
              value={formData.unit}
              onValueChange={(v: InventoryUnit) =>
                setFormData({ ...formData, unit: v })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INVENTORY_UNITS.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="currentStock">Initial Stock</Label>
              <Input
                id="currentStock"
                type="number"
                min="0"
                value={formData.currentStock || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    currentStock: parseInt(e.target.value) || 0,
                  })
                }
                placeholder="e.g., 100"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="minStockLevel">Minimum Stock</Label>
              <Input
                id="minStockLevel"
                type="number"
                min="0"
                value={formData.minStockLevel || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    minStockLevel: parseInt(e.target.value) || 0,
                  })
                }
                placeholder="e.g., 10"
                required
              />
              <p className="text-xs text-muted-foreground">
                Alert when stock falls below this level
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface EditItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: InventoryItem | null;
  onSubmit: (data: {
    name: string;
    sku: string;
    unit: InventoryUnit;
    minStockLevel: number;
  }) => void;
  isLoading: boolean;
}

function EditItemDialog({
  open,
  onOpenChange,
  item,
  onSubmit,
  isLoading,
}: EditItemDialogProps) {
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    unit: "g" as InventoryUnit,
    minStockLevel: 10,
  });

  useEffect(() => {
    if (open && item) {
      setFormData({
        name: item.name,
        sku: item.sku || "",
        unit: (item.unit as InventoryUnit) || "g",
        minStockLevel: item.minStockLevel,
      });
    }
  }, [open, item]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Edit Inventory Item
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Item Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-sku">SKU</Label>
              <Input
                id="edit-sku"
                value={formData.sku}
                onChange={(e) =>
                  setFormData({ ...formData, sku: e.target.value })
                }
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-unit">Unit of Measure</Label>
            <Select
              value={formData.unit}
              onValueChange={(v: InventoryUnit) =>
                setFormData({ ...formData, unit: v })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INVENTORY_UNITS.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-minStockLevel">Minimum Stock Level</Label>
            <Input
              id="edit-minStockLevel"
              type="number"
              min="0"
              value={formData.minStockLevel || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  minStockLevel: parseInt(e.target.value) || 0,
                })
              }
              placeholder="e.g., 10"
              required
            />
            <p className="text-xs text-muted-foreground">
              Alert when stock falls below this level
            </p>
          </div>
          <div className="rounded-lg border bg-muted/50 p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Current Stock
              </span>
              <span className="font-semibold">
                {item.currentStock} {item.unit}
              </span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Use "Adjust Stock" to change the stock level
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface AdjustStockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item:
    | { id: string; name: string; currentStock: number; unit: string }
    | null
    | undefined;
  onSubmit: (data: {
    quantity: number;
    reason: string;
    notes?: string;
  }) => void;
  isLoading: boolean;
}

function AdjustStockDialog({
  open,
  onOpenChange,
  item,
  onSubmit,
  isLoading,
}: AdjustStockDialogProps) {
  const [quantity, setQuantity] = useState(0);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [adjustType, setAdjustType] = useState<"add" | "remove">("add");

  useEffect(() => {
    if (open) {
      setQuantity(0);
      setReason("");
      setNotes("");
      setAdjustType("add");
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalQuantity =
      adjustType === "remove" ? -Math.abs(quantity) : Math.abs(quantity);
    const combinedNotes = notes.trim() ? `${reason}: ${notes.trim()}` : reason;
    onSubmit({ quantity: finalQuantity, reason, notes: combinedNotes });
  };

  if (!item) return null;

  const isRemove = adjustType === "remove";
  const previewQuantity = isRemove ? -Math.abs(quantity) : Math.abs(quantity);
  const newStock = Math.max(0, item.currentStock + previewQuantity);
  const combinedNotesLength = (
    notes.trim() ? `${reason}: ${notes.trim()}` : reason
  ).length;
  const notesTooShort =
    isRemove && quantity > 0 && !!reason && combinedNotesLength < 5;
  const wouldOversell =
    isRemove && quantity > 0 && quantity > item.currentStock;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpDown className="h-5 w-5" />
            Adjust Stock
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-lg border bg-muted/50 p-4 text-center">
            <h3 className="font-semibold">{item.name}</h3>
            <p className="mt-1 text-3xl font-bold">
              {item.currentStock}{" "}
              <span className="text-lg font-normal text-muted-foreground">
                {item.unit}
              </span>
            </p>
            <p className="text-sm text-muted-foreground">Current Stock</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={adjustType === "add" ? "default" : "outline"}
              className={cn(
                adjustType === "add" && "bg-amber-700 hover:bg-amber-800",
              )}
              onClick={() => setAdjustType("add")}
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Add Stock
            </Button>
            <Button
              type="button"
              variant={adjustType === "remove" ? "default" : "outline"}
              className={cn(
                adjustType === "remove" && "bg-stone-700 hover:bg-stone-800",
              )}
              onClick={() => setAdjustType("remove")}
            >
              <TrendingDown className="mr-2 h-4 w-4" />
              Remove Stock
            </Button>
          </div>
          <div className="space-y-2">
            <Label htmlFor="quantity">Quantity</Label>
            <Input
              id="quantity"
              type="number"
              min="1"
              value={quantity || ""}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
              placeholder="e.g., 50"
              className="text-center text-lg"
            />
          </div>
          {quantity > 0 && (
            <div className="rounded-lg border border-dashed p-3 text-center">
              <p className="text-sm text-muted-foreground">New Stock Level</p>
              <p className="text-2xl font-bold">
                {newStock}{" "}
                <span className="text-sm font-normal text-muted-foreground">
                  {item.unit}
                </span>
              </p>
              <p
                className={cn(
                  "text-sm font-medium",
                  adjustType === "add" ? "text-amber-700" : "text-stone-600",
                )}
              >
                {adjustType === "add" ? "+" : "-"}
                {quantity} from current
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {adjustType === "add" ? (
                  <>
                    <SelectItem value="restock">Restock / Delivery</SelectItem>
                    <SelectItem value="count">
                      Inventory Count Correction
                    </SelectItem>
                    <SelectItem value="return">Customer Return</SelectItem>
                    <SelectItem value="transfer">Transfer In</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="waste">Waste / Spoilage</SelectItem>
                    <SelectItem value="damage">Damaged</SelectItem>
                    <SelectItem value="count">
                      Inventory Count Correction
                    </SelectItem>
                    <SelectItem value="transfer">Transfer Out</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">
              Notes{" "}
              {isRemove ? (
                <span className="text-destructive">(required)</span>
              ) : (
                "(optional)"
              )}
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                isRemove
                  ? "Required when removing stock"
                  : "Add any additional details..."
              }
              rows={2}
            />
            {notesTooShort && (
              <p className="text-sm text-destructive">
                Negative adjustments need a note of at least 5 characters.
              </p>
            )}
            {wouldOversell && (
              <p className="text-sm text-destructive">
                Cannot remove more than current stock ({item.currentStock}{" "}
                {item.unit}).
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isLoading ||
                !reason ||
                quantity <= 0 ||
                notesTooShort ||
                wouldOversell
              }
              className={cn(
                adjustType === "add"
                  ? "bg-amber-700 hover:bg-amber-800"
                  : "bg-stone-700 hover:bg-stone-800",
              )}
            >
              {isLoading ? "Saving..." : "Confirm Adjustment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
